import {
  ListaDeAcuerdosSchema,
  PagoATerceroSchema,
  PaginaDeMovimientosSchema,
  PaginaDePagosSchema,
  ResultadoDeCargaDePagosSchema,
  ResumenDeCashflowSchema,
  type EdicionDeAcuerdo,
  type EdicionDePago,
  type Movimiento,
  type NuevoPago,
  type PagoATercero,
  type TablasParaExcel,
  CABECERA_IDIOMA,
  ErrorApiSchema,
} from '@cnc/contracts';
import {keepPreviousData, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import type {ZodType} from 'zod';
import {idiomaActual, tr} from '../i18n/idioma';
import {ErrorDeApi, json, pedir} from './cliente';
import {refrescarDatos} from './refrescar';

// Cashflow (decision 0042): resumen, movimientos, pagos a terceros y acuerdos.
// Con el año y el mes de zarpe de la exportacion (anioEtd, mesEtd).
export function useResumenDeCashflow(cadena = '') {
  return useQuery({
    queryKey: ['cashflow', 'resumen', cadena],
    queryFn: () => pedir(`cashflow/resumen${cadena ? `?${cadena}` : ''}`, ResumenDeCashflowSchema),
    placeholderData: keepPreviousData,
  });
}

export function useMovimientos(cadena: string) {
  return useQuery({
    queryKey: ['cashflow', 'movimientos', cadena],
    queryFn: () => pedir(`cashflow/movimientos${cadena ? `?${cadena}` : ''}`, PaginaDeMovimientosSchema),
    placeholderData: keepPreviousData,
  });
}

export function usePagosATerceros(cadena: string) {
  return useQuery({
    queryKey: ['cashflow', 'pagos', cadena],
    queryFn: () => pedir(`cashflow/pagos${cadena ? `?${cadena}` : ''}`, PaginaDePagosSchema),
    placeholderData: keepPreviousData,
  });
}

export function useAcuerdosDePago() {
  return useQuery({
    queryKey: ['cashflow', 'acuerdos'],
    queryFn: () => pedir('cashflow/acuerdos', ListaDeAcuerdosSchema),
  });
}

export function useEditarPago() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({id, edicion}: {id: number; edicion: EdicionDePago}) =>
      pedir(`cashflow/pagos/${id}`, PagoATerceroSchema, json('PATCH', edicion)),
    onSettled: () => void cliente.invalidateQueries({queryKey: ['cashflow']}),
  });
}

export function useCrearPago() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (nuevo: NuevoPago) => pedir('cashflow/pagos', PagoATerceroSchema, json('POST', nuevo)),
    onSuccess: () => void cliente.invalidateQueries({queryKey: ['cashflow']}),
  });
}

export function useEditarAcuerdo() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({codigo, edicion}: {codigo: string; edicion: EdicionDeAcuerdo}) =>
      pedir(`cashflow/acuerdos/${encodeURIComponent(codigo)}`, ListaDeAcuerdosSchema, json('PATCH', edicion)),
    onSettled: () => void cliente.invalidateQueries({queryKey: ['cashflow']}),
  });
}

export function useCargarPagos() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({archivo, modo}: {archivo: File; modo: 'revisar' | 'aplicar'}) => {
      const cuerpo = new FormData();
      cuerpo.append('archivo', archivo, archivo.name);
      cuerpo.append('modo', modo);
      return pedir('cashflow/pagos/cargar', ResultadoDeCargaDePagosSchema, {method: 'POST', body: cuerpo});
    },
    onSuccess: r => {
      if (r.aplicado) void refrescarDatos(cliente);
    },
  });
}

// Todas las filas de una tabla paginada con los filtros puestos (para el Excel de lo que se ve).
async function todasLasPaginas<T>(
  ruta: string,
  cadena: string,
  esquema: ZodType<{elementos: T[]; totalPaginas: number}>,
): Promise<T[]> {
  const filas: T[] = [];
  for (let pagina = 1; ; pagina++) {
    const consulta = new URLSearchParams(cadena);
    consulta.set('pagina', String(pagina));
    consulta.set('porPagina', '500');
    const r = await pedir(`${ruta}?${consulta.toString()}`, esquema);
    filas.push(...r.elementos);
    if (pagina >= r.totalPaginas) return filas;
  }
}
export const todosLosMovimientos = (cadena: string) =>
  todasLasPaginas<Movimiento>('cashflow/movimientos', cadena, PaginaDeMovimientosSchema);
export const todosLosPagos = (cadena: string) =>
  todasLasPaginas<PagoATercero>('cashflow/pagos', cadena, PaginaDePagosSchema);

// Descarga en Excel las tablas que se ven (una hoja por tabla).
export async function descargarTablasEnExcel(nombre: string, hojas: TablasParaExcel['hojas']) {
  const cuerpo = new FormData();
  cuerpo.append('tablas', new Blob([JSON.stringify({nombre, hojas})], {type: 'application/json'}), 'tablas.json');
  const respuesta = await fetch('/api/cashflow/excel', {
    method: 'POST',
    body: cuerpo,
    credentials: 'same-origin',
    headers: {[CABECERA_IDIOMA]: idiomaActual()},
  });
  if (!respuesta.ok) {
    const error = ErrorApiSchema.safeParse(await respuesta.json().catch(() => null));
    throw new ErrorDeApi(
      error.success ? error.data.codigo : 'DESCONOCIDO',
      error.success ? error.data.error : tr('No se pudo armar el Excel.', 'Could not build the Excel.'),
    );
  }
  const enlace = document.createElement('a');
  enlace.href = URL.createObjectURL(await respuesta.blob());
  enlace.download = `${nombre}.xlsx`;
  enlace.click();
  setTimeout(() => URL.revokeObjectURL(enlace.href), 10_000);
}
